import { Box, For, Table } from '@chakra-ui/react';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import type { DataTableInnerProps } from '@/components/Organisms/DataTable/DataTable.types';
import DataTableColumnFilterOptions from '@/components/Organisms/DataTable/DataTableColumnFilterOptions';
import DataTableRow from '@/components/Organisms/DataTable/DataTableRow';
import DataTableSkeleton from '@/components/Organisms/DataTable/DataTableSkeleton';
import { colors } from '@/components/ui/theme';

const DataTableInner = ({
	columns,
	items,
	isSelectable,
	selection,
	bordered,
	size = 3,
	setSelection,
	actions = [],
	loading,
	sort,
	setSort,
	columnsFilters,
	setColumnFilters,
	allowExpand = true,
	selectColumnRef,
	onSelectColumnRefReady,
	actionMenuOpenOverride = false,
	hideAllColumnFilters = false,
}: DataTableInnerProps) => {
	return (
		<>
			<Table.Root stickyHeader tableLayout="fixed">
				<Table.Header>
					<Table.Row>
						<Show when={!!isSelectable}>
							<Table.ColumnHeader
								border={0}
								fontSize="sm"
								fontWeight={600}
								w="30px"
								p={0}
								transition="all 0.3s ease-in-out"
							>
								{/* This box is added for Expand Hidden Header */}
								<Show when={!hideAllColumnFilters}>
									<Box bg={{ base: 'white', _dark: colors.tabelCellDark }} p={size}>
										<Box height={6}></Box>
									</Box>
								</Show>
								<Box
									bg={{
										base: '{colors.gray.100}',
										_dark: colors.tabelHeaderCellDark,
									}}
									py={size}
									px={0}
								>
									<Box height={5}></Box>
								</Box>
							</Table.ColumnHeader>
						</Show>
						<Show when={allowExpand}>
							<Table.ColumnHeader
								border={0}
								fontSize="sm"
								fontWeight={600}
								w="20px"
								p={0}
								transition="all 0.3s ease-in-out"
							>
								<Show when={!hideAllColumnFilters}>
									<Box bg={{ base: 'white', _dark: colors.tabelCellDark }} py={size} px={0}>
										<Box height={6}></Box>
									</Box>
								</Show>
								<Box
									bg={{
										base: '{colors.gray.100}',
										_dark: colors.tabelHeaderCellDark,
									}}
									py={size}
									px={0}
								>
									<Box height={5}></Box>
								</Box>
							</Table.ColumnHeader>
						</Show>

						<For each={columns}>
							{(col) => (
								<Table.ColumnHeader
									w={col.width || '100px'}
									key={col.key}
									border={0}
									fontSize="sm"
									fontWeight={600}
									p={0}
									transition="all 0.3s ease-in-out"
								>
									<Show when={!hideAllColumnFilters}>
										<Box
											bg={{ base: 'white', _dark: colors.tabelCellDark }}
											p={size}
										>
											<Box height={6}>
												<Show when={!col.hideColumnFilter}>
													<DataTableColumnFilterOptions
														column={col}
														sort={sort}
														setSort={setSort}
														columnsFilters={columnsFilters}
														setColumnFilters={setColumnFilters}
													/>
												</Show>
											</Box>
										</Box>
									</Show>
									<Box
										bg={{
											base: '{colors.gray.100}',
											_dark: colors.tabelHeaderCellDark,
										}}
										p={size}
									>
										{col.label}
									</Box>
								</Table.ColumnHeader>
							)}
						</For>

						<Show when={actions.length > 0}>
							<Table.ColumnHeader
								border={0}
								fontSize="sm"
								fontWeight={600}
								w="60px"
								p={0}
								transition="all 0.3s ease-in-out"
							>
								<Show when={!hideAllColumnFilters}>
									<Box bg={{ base: 'white', _dark: colors.tabelCellDark }} p={size}>
										<Box height={6}></Box>
									</Box>
								</Show>
								<Box
									bg={{
										base: '{colors.gray.100}',
										_dark: colors.tabelHeaderCellDark,
									}}
									p={size}
								>
									Action
								</Box>
							</Table.ColumnHeader>
						</Show>
					</Table.Row>
				</Table.Header>
				<Table.Body transition="all 0.3s ease-in-out">
					<Show when={loading}>
						<DataTableSkeleton
							bordered={bordered}
							size={size}
							totalColumns={
								columns.length + 1 +
								(isSelectable ? 1 : 0) +
								(actions.length > 0 ? 1 : 0)
							}
							isSelectable={isSelectable}
						/>
					</Show>
					<Show when={!loading}>
						<For each={items}>
							{(item, index) => (
							<DataTableRow
								key={item.id}
								item={item}
								columns={columns}
								isSelectable={isSelectable}
								selection={selection}
								setSelection={setSelection}
								bordered={bordered}
								actions={actions}
								size={size}
								allowExpand={allowExpand}
								selectColumnRef={index === 0 ? selectColumnRef : undefined}
								onSelectColumnRefReady={index === 0 ? onSelectColumnRefReady : undefined}
								actionMenuOpenOverride={
									typeof actionMenuOpenOverride === 'function'
										? actionMenuOpenOverride(item, index)
										: actionMenuOpenOverride && index === 0
								}
							/>
							)}
						</For>
					</Show>
				</Table.Body>

			</Table.Root>
			<Show when={!loading && (!items || items.length === 0)}>
				<FaceliftText textAlign={'center'} width={'full'} my={'10'}>No Data Available</FaceliftText>
			</Show>
		</>
	);
};

export default DataTableInner;
